/**
 * URL helpers for the Chat app (opened from profile pictures, header, search).
 */

import { config } from '@config/env';

const LOCALHOST_PATTERN = /^(https?:\/\/)?(localhost|127\.0\.0\.1)([:/]|$)/i;

function ensureProtocol(url: string): string
{
  if (/^https?:\/\//i.test(url)) return url;
  return 'https://' + url;
}

function isLocalhostUrl(url: string): boolean
{
  return LOCALHOST_PATTERN.test(url.trim());
}

function isRunningOnLocalhost(): boolean
{
  return /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
}

/**
 * Absolute base URL of the Chat app, without trailing slash.
 * Order: VITE_JS_webURLChat, then VITE_JS_webURL + Chat segment, then the current origin.
 * A localhost URL from the env is ignored when the app is not running on localhost.
 */
export function getChatBaseUrl(): string
{
  const chatSegment = config.web.chatVersion === 'v2' ? 'ChatV2' : 'Chat';
  const allowLocalhost = isRunningOnLocalhost();

  const chatUrl = (config.web.chatUrl || '').trim();
  if (chatUrl && (allowLocalhost || !isLocalhostUrl(chatUrl)))
  {
    return ensureProtocol(chatUrl).replace(/\/+$/, '');
  }

  const webUrl = (config.web.url || '').trim();
  if (webUrl && (allowLocalhost || !isLocalhostUrl(webUrl)))
  {
    return ensureProtocol(webUrl).replace(/\/+$/, '') + '/' + chatSegment;
  }

  return window.location.origin + getAppBasePath() + '/' + chatSegment;
}

// Resolve the app base path (e.g. '/V2' on QA, '' on localhost) so the chat
// link follows the environment it is opened from.
export function getAppBasePath(): string
{
  const buildBase = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');
  if (buildBase) return buildBase;

  const match = window.location.pathname.match(/^(\/[^/]+)(?:\/|$)/);
  const appRoutes = ['/home', '/bookmarks', '/dashboards', '/dashboard', '/projectdetails', '/addpeople', '/recyclebin'];
  if (match?.[1] && !appRoutes.includes(match[1].toLowerCase())) return match[1];
  return '';
}

/** Absolute URL that opens a private chat window in the Chat app. */
export function buildChatWindowUrl(chatId: number | string): string
{
  return getChatBaseUrl() + '/#/OpenWindow/' + chatId;
}
